import { AxiosError } from 'axios';
import { useMutation } from '@tanstack/react-query';
import {
  SuggestedRouteMetricsType,
  // Route Service V2 types
  AddLegParams,
  UpdateLegParams,
  LegIdentifiers,
  RouteIdentifiers,
  AddTmdxJetAviaLeg,
  AddSupplementalLiftAviaLeg,
  AddTxCenterProviderAviaLeg,
  AddTmdxAffiliateGroundLeg,
  AddTxCenterProviderGroundLeg,
  AddCurriServiceGroundLeg,
  AddTmdxDriveAffiliateGroundLeg,
  AddTmdxDriveTedsGroundLeg,
  AddSelfServicedGroundLeg,
  AddAccommodationLeg,
  AddCommercialAviaLeg,
  AddSabreCommercialAviaLegs,
  ModifySabreCommercialAviaLegs,
  SuggestedRoutesParams,
  LogisticsVersionIdentifier,
  ResourceIdentifiers,
  RemoveLogisticsRoutesParams,
  HubsRequestParams,
  HubsDataRequestList,
} from '@/api/apis/route';
import { api } from '@/api';
import {
  addHubsToRoute,
  handleLogisticsError,
  refetchRouteLogistics,
  removeHubsFromRoute,
  removeLogisticsRoutes,
} from '@/queries/route-planner/utils';

export const useSetBenchmark = () => {
  return useMutation({
    mutationFn: (data: { caseId: string; taskId: string }) => api.route.setBenchmark(data),
  });
};

export const useSendSuggestedRoutesMetrics = () => {
  return useMutation({
    mutationFn: (data: SuggestedRouteMetricsType) => api.route.sendSuggestedRoutesMetrics(data),
  });
};

// Route Service V2 Mutations
// Suggested Routes
export const useAddSuggestedRoutes = () => {
  return useMutation({
    mutationFn: (params: SuggestedRoutesParams) => api.route.addSuggestedRoutes(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

export const useAddPartialSuggestedRoutes = () => {
  return useMutation({
    mutationFn: (params: SuggestedRoutesParams) => api.route.addPartialSuggestedRoutes(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

// TMDX Avia Leg
export const useAddTmdxAviaLeg = () => {
  return useMutation({
    mutationFn: (params: AddLegParams<AddTmdxJetAviaLeg>) => api.route.addTmdxAviaLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

export const useUpdateTmdxAviaLeg = () => {
  return useMutation({
    mutationFn: (params: UpdateLegParams<AddTmdxJetAviaLeg>) => api.route.updateTmdxAviaLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

// Supplemental Lift Avia Leg
export const useAddSupplementalLiftLeg = () => {
  return useMutation({
    mutationFn: (params: AddLegParams<AddSupplementalLiftAviaLeg>) => api.route.addSupplementalLiftLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

export const useUpdateSupplementalLiftLeg = () => {
  return useMutation({
    mutationFn: (params: UpdateLegParams<AddSupplementalLiftAviaLeg>) => api.route.updateSupplementalLiftLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

// TX Center Avia Leg
export const useAddTxCenterAviaLeg = () => {
  return useMutation({
    mutationFn: (params: AddLegParams<AddTxCenterProviderAviaLeg>) => api.route.addTxCenterAviaLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

export const useUpdateTxCenterAviaLeg = () => {
  return useMutation({
    mutationFn: (params: UpdateLegParams<AddTxCenterProviderAviaLeg>) => api.route.updateTxCenterAviaLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

// Commercial Flight Leg
export const useAddCommercialLeg = () => {
  return useMutation({
    mutationFn: (params: AddLegParams<AddCommercialAviaLeg>) => api.route.addCommercialLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

export const useAddSabreCommercialLegs = () => {
  return useMutation({
    mutationFn: (params: AddLegParams<AddSabreCommercialAviaLegs>) => api.route.addSabreCommercialLegs(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

export const useUpdateSabreCommercialLeg = () => {
  return useMutation({
    mutationFn: (params: UpdateLegParams<ModifySabreCommercialAviaLegs>) => api.route.updateSabreCommercialLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

export const useUpdateCommercialLeg = () => {
  return useMutation({
    mutationFn: (params: UpdateLegParams<AddCommercialAviaLeg>) => api.route.updateCommercialLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

export const useRemoveSabreCommercialLegs = () => {
  return useMutation({
    mutationFn: (params: RouteIdentifiers & LogisticsVersionIdentifier & { sabreGroupId: string }) =>
      api.route.removeSabreCommercialLegs(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

// TMDX Affiliate Ground Leg
export const useAddTmdxAffiliateLeg = () => {
  return useMutation({
    mutationFn: (params: AddLegParams<AddTmdxAffiliateGroundLeg>) => api.route.addTmdxAffiliateLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

export const useUpdateTmdxAffiliateLeg = () => {
  return useMutation({
    mutationFn: (params: UpdateLegParams<AddTmdxAffiliateGroundLeg>) => api.route.updateTmdxAffiliateLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

// TMDX Drive Affiliate Ground Leg
export const useAddTmdxDriveAffiliateLeg = () => {
  return useMutation({
    mutationFn: (params: AddLegParams<AddTmdxDriveAffiliateGroundLeg>) => api.route.addTmdxDriveAffiliateLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

export const useUpdateTmdxDriveAffiliateLeg = () => {
  return useMutation({
    mutationFn: (params: UpdateLegParams<AddTmdxDriveAffiliateGroundLeg>) =>
      api.route.updateTmdxDriveAffiliateLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

// TX Center Ground Leg
export const useAddTxCenterGroundLeg = () => {
  return useMutation({
    mutationFn: (params: AddLegParams<AddTxCenterProviderGroundLeg>) => api.route.addTxCenterGroundLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

export const useUpdateTxCenterGroundLeg = () => {
  return useMutation({
    mutationFn: (params: UpdateLegParams<AddTxCenterProviderGroundLeg>) => api.route.updateTxCenterGroundLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

// Curri Service Ground Leg
export const useAddCurriServiceLeg = () => {
  return useMutation({
    mutationFn: (params: AddLegParams<AddCurriServiceGroundLeg>) => api.route.addCurriServiceLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

export const useUpdateCurriServiceLeg = () => {
  return useMutation({
    mutationFn: (params: UpdateLegParams<AddCurriServiceGroundLeg>) => api.route.updateCurriServiceLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

// TMDX Drive Teds Ground Leg
export const useAddTmdxDriveTedsLeg = () => {
  return useMutation({
    mutationFn: (params: AddLegParams<AddTmdxDriveTedsGroundLeg>) => api.route.addTmdxDriveTedsLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

export const useUpdateTmdxDriveTedsLeg = () => {
  return useMutation({
    mutationFn: (params: UpdateLegParams<AddTmdxDriveTedsGroundLeg>) => api.route.updateTmdxDriveTedsLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

// Self Serviced Ground Leg
export const useAddSelfServicedGroundLeg = () => {
  return useMutation({
    mutationFn: (params: AddLegParams<AddSelfServicedGroundLeg>) => api.route.addSelfServicedGroundLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

export const useUpdateSelfServicedGroundLeg = () => {
  return useMutation({
    mutationFn: (params: UpdateLegParams<AddSelfServicedGroundLeg>) => api.route.updateSelfServicedGroundLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

// Accommodation Leg
export const useAddAccommodationLeg = () => {
  return useMutation({
    mutationFn: (params: AddLegParams<AddAccommodationLeg>) => api.route.addAccommodationLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

export const useUpdateAccommodationLeg = () => {
  return useMutation({
    mutationFn: (params: UpdateLegParams<AddAccommodationLeg>) => api.route.updateAccommodationLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

// Remove Leg
export const useRemoveLeg = () => {
  return useMutation({
    mutationFn: (params: LegIdentifiers & LogisticsVersionIdentifier) => api.route.removeLeg(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

// Remove Routes
export const useRemoveRoute = () => {
  return useMutation({
    mutationFn: (params: RemoveLogisticsRoutesParams) => removeLogisticsRoutes(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

// Remove Resource
export const useRemoveResource = () => {
  return useMutation({
    mutationFn: (params: ResourceIdentifiers & LogisticsVersionIdentifier) => api.route.removeResource(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

// Hub Operations
export const useSetRouteHubs = () => {
  return useMutation({
    mutationFn: (params: HubsRequestParams<HubsDataRequestList>) => addHubsToRoute(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};

export const useRemoveRouteHubs = () => {
  return useMutation({
    mutationFn: (params: HubsRequestParams) => removeHubsFromRoute(params),
    onSuccess: async (_, variables) => refetchRouteLogistics(variables.logisticsId),
    onError: (error, variables) => handleLogisticsError(error as AxiosError, variables),
  });
};
